import { View, StyleSheet, ScrollView, Text, BackHandler, Platform } from 'react-native';
import { Stack, useLocalSearchParams, usePathname, useRouter } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import React, { useEffect } from 'react';
import Header from '../../../../components/Header';
import Footer from '../../../../components/Footer';
import MetaTags from '../../../../components/MetaTags';
import AppRouteBootScreen from '../../../../components/auth/AppRouteBootScreen';
import Banner from '../../../../components/ui/Banner';
import DeliveryContent from '../../../../components/DeliveryContent';
import { ProjectProvider, useProject } from '../../../../lib/ProjectContext';
import { useSession } from '../../../../lib/SessionContext';
import colors from '../../../../lib/colors';
import { buildCanonicalDashboardPath, buildDashboardPath } from '../../../../lib/dashboardRoutes';
import { isProjectActivated } from '../../../../lib/projectActivation';
import { hasConfirmedEmailIdentity } from '../../../../utils/authHelpers';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';

function ProjectDeliveryContent() {
    const { digestId, from, setup_source } = useLocalSearchParams<{
        digestId: string;
        from?: string;
        setup_source?: string;
    }>();
    const pathname = usePathname();
    const router = useRouter();
    const { project, isLoading, error } = useProject();
    const { session, isLoading: sessionLoading } = useSession();

    const isAnonymous = !sessionLoading && !hasConfirmedEmailIdentity(session);
    const sessionFirstName = typeof session?.user?.user_metadata?.first_name === 'string'
        ? session.user.user_metadata.first_name
        : null;

    // Show cancel button when coming from edit
    const showCancelButton = from === 'edit';
    const shouldBlockBackNavigation = from !== 'edit';
    const deliveryPath = buildDashboardPath(pathname, `/${digestId}/delivery`);
    const normalizedPathname = (pathname || '').replace(/\/+$/, '');
    const isCurrentDeliveryRoute = normalizedPathname === deliveryPath;
    const needsActivation = Boolean(project) && !isProjectActivated(project) && from !== 'edit';
    const canRenderDelivery = Boolean(project) && !error && !needsActivation;

    useEffect(() => {
        if (!needsActivation) return;
        router.replace(buildDashboardPath(pathname, `/${digestId}/activate`));
    }, [digestId, needsActivation, pathname, router]);

    useEffect(() => {
        if (!shouldBlockBackNavigation || !isCurrentDeliveryRoute) {
            return;
        }

        if (Platform.OS === 'web' && typeof window !== 'undefined') {
            const deliveryRoute = buildDashboardPath(
                pathname,
                `/${digestId}/delivery${from ? `?from=${from}` : ''}`
            );
            const lockState = { deliveryBackLocked: true };

            const handlePopState = () => {
                window.history.pushState(lockState, '', deliveryRoute);
                router.replace(deliveryRoute);
            };

            window.addEventListener('popstate', handlePopState);
            return () => {
                window.removeEventListener('popstate', handlePopState);
            };
        }

        const subscription = BackHandler.addEventListener('hardwareBackPress', () => true);
        return () => {
            subscription.remove();
        };
    }, [shouldBlockBackNavigation, isCurrentDeliveryRoute, digestId, from, router]);

    return (
        <>
            <MetaTags
                title={`Delivery & Meta - ${project?.name || 'Project'} - Paperzilla`}
                description="Project delivery options and metadata management"
                url={`https://paperzilla.ai${buildCanonicalDashboardPath(`/${digestId}/delivery`)}`}
            />
            <Stack.Screen options={{ headerShown: false }} />
            <View style={styles.page}>
                <Header />
                <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
                    <View style={styles.contentWrapper}>
                    <View style={styles.content}>
                    {error && (
                        <Banner
                            type="error"
                            title={msg({ id: 'projectDelivery.loadErrorTitle', message: 'Error Loading Project' })}
                            message={msg({
                                id: 'projectDelivery.loadErrorMessage',
                                message: "We couldn't load this project. Please try again.",
                            })}
                            dismissable
                        />
                    )}
                    {!error && !project && isLoading && (
                        <Banner
                            type="loading"
                            message={msg({ id: 'projectDelivery.loading', message: 'Loading project setup...' })}
                        />
                    )}
                    {!error && !project && !isLoading && (
                        <Banner
                            type="error"
                            title={msg({ id: 'projectDelivery.notFoundTitle', message: 'Project Not Found' })}
                            message={msg({
                                id: 'projectDelivery.notFoundMessage',
                                message: "This project doesn't exist or you don't have access to it.",
                            })}
                            dismissable
                        />
                    )}
                    {canRenderDelivery && (
                        <DeliveryContent
                            projectId={digestId}
                            project={project}
                            isLoading={isLoading}
                            isPreview={isAnonymous}
                            sessionFirstName={sessionFirstName}
                            showCancelButton={showCancelButton}
                            setupSource={setup_source || null}
                        />
                    )}
                    </View>
                    </View>
                    <Footer />
                </ScrollView>
                <StatusBar style="auto" />
            </View>
        </>
    );
}

export default function ProjectDeliveryScreen() {
    const { digestId, setup_source } = useLocalSearchParams<{
        digestId: string;
        setup_source?: string;
    }>();
    const pathname = usePathname();
    const router = useRouter();
    const { session, isLoading: sessionLoading } = useSession();
    const projectId = Array.isArray(digestId) ? digestId[0] : digestId;
    const setupSource = Array.isArray(setup_source) ? setup_source[0] : setup_source;
    const canOpenDelivery = hasConfirmedEmailIdentity(session);

    useEffect(() => {
        if (sessionLoading || !projectId || canOpenDelivery) return;

        const query = setupSource
            ? `?setup_source=${encodeURIComponent(setupSource)}`
            : '';
        router.replace(buildDashboardPath(pathname, `/${projectId}/activate${query}`));
    }, [canOpenDelivery, pathname, projectId, router, sessionLoading, setupSource]);

    if (!projectId) {
        return (
            <View style={styles.errorContainer}>
                <Text style={styles.errorText}><Trans id="digests.byDigestid.delivery.noProjectIdProvided" comment="UI copy in app/(funnel)/digests/[digestId]/delivery.tsx">No project ID provided</Trans></Text>
            </View>
        );
    }

    if (sessionLoading || !canOpenDelivery) {
        return <AppRouteBootScreen />;
    }

    return (
        <ProjectProvider projectId={projectId} allowTransientNotFoundRetry>
            <ProjectDeliveryContent />
        </ProjectProvider>
    );
}

const styles = StyleSheet.create({
    page: {
        flex: 1,
        backgroundColor: colors.bgWeak,
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        flexGrow: 1,
        paddingHorizontal: 0,
        paddingBottom: 0,
    },
    contentWrapper: {
        flex: 1,
    },
    content: {
        width: '100%',
        maxWidth: 1200,
        alignSelf: 'center',
    },
    errorContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    errorText: {
        fontSize: 16,
        color: colors.errorRed,
        textAlign: 'center',
    },
});
